/**
 * Map GeolocationPositionError to a short message.
 * @param {GeolocationPositionError} err
 */
function positionErrorMessage(err) {
  if (err.code === 1) return 'Location permission denied';
  if (err.code === 2) return 'Location unavailable';
  if (err.code === 3) return 'Location request timed out';
  return err.message || 'Location error';
}

/**
 * Single getCurrentPosition read.
 * @param {{ enableHighAccuracy?: boolean; timeout?: number; maximumAge?: number }} opts
 */
function getPositionOnce(opts) {
  const { enableHighAccuracy, timeout, maximumAge } = opts;
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
          capturedAt: new Date().toISOString()
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: !!enableHighAccuracy,
        maximumAge: maximumAge ?? 0,
        timeout: timeout ?? 20000
      }
    );
  });
}

/**
 * Fresh position for attendance: try GPS/high accuracy first, then Wi‑Fi / cell (faster indoors, laptops).
 * @param {{ highAccuracyTimeout?: number; lowAccuracyTimeout?: number }} opts
 * @returns {Promise<{ lat: number; lng: number; accuracy: number; capturedAt: string }>}
 */
export async function getCurrentPositionFresh(opts = {}) {
  const highT = opts.highAccuracyTimeout ?? opts.highTimeout ?? 14000;
  const lowT = opts.lowAccuracyTimeout ?? opts.lowTimeout ?? 22000;

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('Geolocation is not supported');
  }

  try {
    return await getPositionOnce({
      enableHighAccuracy: true,
      timeout: highT,
      maximumAge: 0
    });
  } catch (err) {
    if (err && typeof err === 'object' && 'code' in err && err.code === 1) {
      throw new Error(positionErrorMessage(err));
    }
    try {
      return await getPositionOnce({
        enableHighAccuracy: false,
        timeout: lowT,
        maximumAge: 0
      });
    } catch (err2) {
      throw new Error(positionErrorMessage(err2));
    }
  }
}
